import { Link } from 'react-router-dom'
import { AuthGoogleButton } from '../components/AuthGoogleButton'
import { AuthGoogleHost } from '../components/AuthGoogleHost'
import { AuthIllustration } from '../components/AuthIllustration'
import { ButtonSpinner } from '../components/ButtonSpinner'
import { AUTH_LOADING, AUTH_LOGIN, AUTH_PATHS } from '../constants/auth'
import { authSubmitLabel } from '../helpers/authLabels'
import { cx } from '../helpers/format'
import {
  isGoogleLoginEnabled,
  isOtpLoginEnabled,
  storefrontAuthLead,
} from '../helpers/settings'
import { useGoogleSignIn } from '../hooks/useGoogleSignIn'
import { useLoginForm } from '../hooks/useLoginForm'
import { useStorefrontSettings } from '../hooks/useStorefrontSettings'
import '../styles/auth-pages.scss'

export function LoginPage() {
  const { settings, loading } = useStorefrontSettings()
  const showOtp = !loading && isOtpLoginEnabled(settings.loginMethod)
  const showGoogle = !loading && isGoogleLoginEnabled(settings.loginMethod)
  const {
    mobile,
    setMobile,
    otp,
    setOtp,
    otpSent,
    message,
    error,
    pending,
    submitting,
    onSubmit,
    onSendOtp,
  } = useLoginForm()
  const google = useGoogleSignIn(showGoogle, settings.googleClientId)

  const primaryFallback = otpSent ? AUTH_LOGIN.submitMobile : AUTH_LOGIN.sendOtp
  const primaryLabel = authSubmitLabel(pending, primaryFallback)
  const primaryBusy = pending === 'otp-request' || pending === 'otp-verify'
  const busy = submitting || google.googlePending
  const pageError = error ?? google.googleError
  const lead = storefrontAuthLead(settings.loginMethod, AUTH_LOGIN)

  return (
    <div className="auth-page">
      <div className={cx('auth-shell', busy && 'is-busy')}>
        <AuthIllustration />

        <section className="auth-card" aria-busy={busy}>
          {busy ? <div className="auth-card__overlay" aria-hidden="true" /> : null}

          <header className="auth-card__header">
            <h1>{AUTH_LOGIN.title}</h1>
            <p>{loading ? AUTH_LOADING.settings : lead}</p>
          </header>

          {showGoogle ? (
            google.useFallbackButton ? (
              <AuthGoogleButton
                label={AUTH_LOGIN.googleLabel}
                onClick={() => void google.onFallbackClick()}
                disabled={busy}
                loading={google.googlePending}
              />
            ) : (
              <AuthGoogleHost hostRef={google.hostRef} />
            )
          ) : null}

          {showGoogle && showOtp ? (
            <p className="auth-divider">{AUTH_LOGIN.divider}</p>
          ) : null}

          {showOtp ? (
            <form className="auth-form" onSubmit={onSubmit}>
              <div className="auth-field">
                <label htmlFor="login-mobile">{AUTH_LOGIN.mobileLabel}</label>
                <div className="auth-mobile">
                  <span className="auth-mobile__code">{AUTH_LOGIN.countryCode}</span>
                  <input
                    id="login-mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder={AUTH_LOGIN.mobilePlaceholder}
                    value={mobile}
                    onChange={(event) =>
                      setMobile(event.target.value.replace(/\D/g, '').slice(0, 10))
                    }
                    required
                    minLength={10}
                    maxLength={10}
                    disabled={busy}
                  />
                </div>
              </div>

              {otpSent ? (
                <div className="auth-field">
                  <div className="auth-field__row">
                    <label htmlFor="login-otp">{AUTH_LOGIN.otpLabel}</label>
                    <button
                      type="button"
                      className="auth-forgot"
                      onClick={() => void onSendOtp()}
                      disabled={busy}
                    >
                      {AUTH_LOGIN.resendOtp}
                    </button>
                  </div>
                  <input
                    id="login-otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={AUTH_LOGIN.otpPlaceholder}
                    value={otp}
                    onChange={(event) =>
                      setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    required
                    minLength={4}
                    maxLength={6}
                    disabled={busy}
                  />
                </div>
              ) : null}

              <button type="submit" className="auth-btn" disabled={busy}>
                {primaryBusy ? <ButtonSpinner label={primaryLabel} /> : primaryFallback}
              </button>
            </form>
          ) : null}

          <p className="auth-privacy">
            {AUTH_LOGIN.privacyBefore}{' '}
            <Link to={AUTH_PATHS.privacy}>{AUTH_LOGIN.privacyLink}</Link>
            {AUTH_LOGIN.privacyAfter}
          </p>

          {pageError ? (
            <p className="auth-message auth-message--error" role="alert">
              {pageError}
            </p>
          ) : null}
          {message ? <p className="auth-message">{message}</p> : null}

          <p className="auth-switch">
            {AUTH_LOGIN.switchPrompt}{' '}
            <Link to={AUTH_PATHS.signup}>{AUTH_LOGIN.switchAction}</Link>
          </p>
        </section>
      </div>
    </div>
  )
}
